import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/admin", icon: "LayoutDashboard", label: "Overview", end: true, key: "overview" },
  { to: "/admin/customers", icon: "Users", label: "Customers", key: "customers" },
  { to: "/admin/analytics", icon: "BarChart3", label: "Analytics", key: "analytics" },
  { to: "/admin/administrators", icon: "UserCog", label: "Administrators", key: "administrators", ownerOnly: true },
  { to: "/admin/qrcodes", icon: "QrCode", label: "QR Codes", key: "qrcodes" },
  { to: "/admin/qr-types", icon: "ListTree", label: "QR Types", key: "qr-types" },
  { to: "/admin/menus", icon: "Utensils", label: "Restaurant Menus", key: "menus" },
  { to: "/admin/plans", icon: "Layers", label: "Plans & Features", key: "plans" },
  { to: "/admin/domains", icon: "Globe", label: "Custom Domains", key: "customers" },
  { to: "/admin/billing", icon: "CreditCard", label: "Billing", key: "billing" },
  { to: "/admin/coupons", icon: "Ticket", label: "Coupons", key: "coupons", ownerOnly: true },
  { to: "/admin/gateways", icon: "Wallet", label: "Payment Gateways", key: "gateways", ownerOnly: true },
  { to: "/admin/email-preview", icon: "Mail", label: "Email Preview", key: "email-preview" },
  { to: "/admin/email-settings", icon: "Send", label: "Email & SMTP", key: "email-settings", ownerOnly: true },
  { to: "/admin/branding", icon: "Palette", label: "Branding", key: "branding", ownerOnly: true },
  { to: "/admin/cms", icon: "FileText", label: "Website Pages", key: "cms", ownerOnly: true },
  { to: "/admin/blog", icon: "Newspaper", label: "Blog", key: "blog" },
  { to: "/admin/blog-comments", icon: "MessageSquare", label: "Blog Comments", key: "blog-comments" },
  { to: "/admin/media", icon: "Image", label: "Media Library", key: "media", ownerOnly: true },
  { to: "/admin/qr-controls", icon: "QrCode", label: "System", key: "system", ownerOnly: true },
  { to: "/admin/profile-fields", icon: "ListChecks", label: "Profile Fields", key: "profile-fields", ownerOnly: true },
  { to: "/admin/support", icon: "LifeBuoy", label: "Support", key: "support" },
  { to: "/admin/audit-logs", icon: "ScrollText", label: "Audit Logs", key: "audit-logs" },
  { to: "/admin/settings", icon: "Settings", label: "Settings", key: "settings" },
];

export default function AdminLayout({ title, subtitle, actions, children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [me, setMe] = useState(null); // null=checking, false=denied, obj=ok
  const [open, setOpen] = useState(false); // mobile drawer
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("adminSidebarCollapsed") === "1");

  useEffect(() => {
    api.get("/admin/me").then(({ data }) => {
      if (data.is_platform_admin) setMe(data);
      else { setMe(false); nav("/admin/login", { replace: true }); }
    }).catch(() => { setMe(false); nav("/admin/login", { replace: true }); });
  }, []); // eslint-disable-line

  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("adminSidebarCollapsed", c ? "0" : "1"); return !c; });
  const active = (n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));
  const logout = async () => { try { await api.post("/admin/logout"); } catch {} nav("/admin/login", { replace: true }); };

  if (me === null) return <div className="min-h-screen grid place-items-center bg-background"><Icon name="Loader2" className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!me) return null;

  // `mini` = icon-only rail on desktop; the mobile drawer always shows full labels.
  const renderSide = (mini = false) => (
    <div className="flex h-full w-full min-w-0 flex-col overflow-x-hidden">
      <div className={`h-16 flex items-center border-b border-border shrink-0 ${mini ? "justify-center px-0" : "px-4 gap-2"}`}>
        {mini ? (
          <div className="h-9 w-9 rounded-lg bg-primary/15 grid place-items-center"><Icon name="ShieldCheck" className="h-5 w-5 text-primary" /></div>
        ) : (
          <Link to="/admin" className="flex items-center gap-2 min-w-0">
            <Logo className="h-7 w-auto shrink-0" />
            <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded shrink-0">Admin</span>
          </Link>
        )}
      </div>

      <nav className="flex-1 min-h-0 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden no-scrollbar">
        {NAV.filter((n) => !n.ownerOnly || me.platform_role === "owner").map((n) => (
          <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.key}`} title={mini ? n.label : undefined} onClick={() => setOpen(false)}
            className={`flex items-center rounded-xl text-sm font-medium transition-colors w-full min-w-0 ${mini ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} ${active(n) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
            <Icon name={n.icon} className="h-4 w-4 shrink-0" />
            {!mini && <span className="truncate">{n.label}</span>}
          </Link>
        ))}
      </nav>

      <div className="p-2 border-t border-border shrink-0">
        <div className={`flex items-center rounded-xl w-full min-w-0 ${mini ? "justify-center py-2" : "gap-3 px-2 py-2"}`}>
          <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center text-sm font-bold text-primary shrink-0">{(me.name || me.email || "A")[0].toUpperCase()}</div>
          {!mini && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate" data-testid="admin-identity" title={me.name || me.email}>{me.name || "Admin"}</div>
                <div className="text-xs text-muted-foreground truncate capitalize">{me.platform_role || "admin"}</div>
              </div>
              <button data-testid="admin-logout" onClick={logout} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-accent shrink-0" title="Log out"><Icon name="LogOut" className="h-4 w-4" /></button>
            </>
          )}
        </div>
        {mini && (
          <button data-testid="admin-logout" onClick={logout} className="mt-1 h-8 w-full grid place-items-center rounded-lg hover:bg-accent" title="Log out"><Icon name="LogOut" className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );

  const asideW = collapsed ? "lg:w-[76px]" : "lg:w-64";
  const mainPad = collapsed ? "lg:pl-[76px]" : "lg:pl-64";

  return (
    <div className="min-h-screen bg-background" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside data-testid="admin-sidebar" className={`hidden lg:flex fixed inset-y-0 left-0 ${asideW} border-r border-border bg-card/50 glass z-30 overflow-x-hidden transition-[width] duration-200`}>
        {renderSide(collapsed)}
      </aside>

      {/* Mobile drawer */}
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 max-w-[80vw] bg-card border-r border-border z-50 overflow-x-hidden transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>
        {renderSide(false)}
      </aside>

      <div className={mainPad}>
        <header className="h-16 border-b border-border glass bg-background/80 sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-2">
            <button className="lg:hidden h-9 w-9 grid place-items-center rounded-xl border border-border" onClick={() => setOpen(true)} data-testid="admin-menu-open"><Icon name="Menu" className="h-4 w-4" /></button>
            <button className="hidden lg:grid h-9 w-9 place-items-center rounded-xl border border-border hover:bg-accent" onClick={toggleCollapse} data-testid="admin-collapse-toggle" title={collapsed ? "Expand sidebar" : "Collapse sidebar"}><Icon name={collapsed ? "PanelLeftOpen" : "PanelLeftClose"} className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app" data-testid="admin-back-to-app" className="text-xs font-semibold text-muted-foreground hover:text-foreground px-3 py-2">← Back to app</Link>
            <ThemeToggle />
          </div>
        </header>
        <main className="p-4 lg:p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div><h1 className="text-2xl font-display font-extrabold">{title}</h1>{subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}</div>
              {actions}
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
